import {TrustStatus} from '@sollapay/enums';
import {MoreMenu} from '@sollapay/ui/components';
import {useCopyToClipboard} from '@sollapay/web-core';
import {useTranslation} from 'react-i18next';

import type {MoreMenuItem, MoreMenuProps} from '@sollapay/ui/components';
import type {FC} from 'react';

export type TrustAccountMenuAction = 'edit' | 'delete';

/**
 * Which actions the account's *status* allows. Permission gating happens separately, by
 * passing `undefined` for a disallowed action's handler — {@link TrustAccountMoreMenu}
 * drops any action without one.
 */
export const getTrustAccountMenuActions = (trustStatus: TrustStatus): TrustAccountMenuAction[] => {
  if (trustStatus === TrustStatus.SETUP_IN_PROGRESS) return ['edit', 'delete'];
  if (trustStatus === TrustStatus.TRUST_ACTIVE) return ['edit'];
  return [];
};

export interface TrustAccountMenuHandlers {
  actions: TrustAccountMenuAction[];
  onEdit?: () => void;
  onDelete?: () => void;
}

/** The account menu's items, for menus with their own trigger (e.g. the share split button). */
export function useTrustAccountMenuItems({
  actions,
  onEdit,
  onDelete
}: TrustAccountMenuHandlers): MoreMenuItem[] {
  const {t} = useTranslation('trustAccounts');

  const actionConfig: Record<TrustAccountMenuAction, MoreMenuItem> = {
    edit: {
      icon: 'pen',
      label: t('tableActions.editAccount'),
      onClick: () => onEdit?.()
    },
    delete: {
      icon: 'delete',
      label: t('tableActions.deleteAccount'),
      onClick: () => onDelete?.(),
      destructive: true,
      separator: true
    }
  };

  const handlerMap: Record<TrustAccountMenuAction, (() => void) | undefined> = {
    edit: onEdit,
    delete: onDelete
  };

  return actions.filter(action => !!handlerMap[action]).map(action => actionConfig[action]);
}

/** `refId` adds a first "copy account number" item. */
export interface TrustAccountMoreMenuProps extends TrustAccountMenuHandlers {
  orientation?: MoreMenuProps['orientation'];
  variant?: MoreMenuProps['variant'];
  refId?: string;
}

export const TrustAccountMoreMenu: FC<TrustAccountMoreMenuProps> = ({
  orientation,
  variant,
  refId,
  ...handlers
}) => {
  const {t} = useTranslation('trustAccounts');
  const {copy} = useCopyToClipboard();
  const actionItems = useTrustAccountMenuItems(handlers);
  const items: MoreMenuItem[] = refId
    ? [
        {
          icon: 'copy',
          label: t('tableActions.copyAccountNumber'),
          onClick: () => void copy(refId, {fieldName: t('accountNumber.label')})
        },
        ...actionItems
      ]
    : actionItems;

  return (
    <MoreMenu
      ariaLabel={t('tableActions.openMenu')}
      orientation={orientation}
      variant={variant}
      items={items}
    />
  );
};
